import { Injectable } from '@angular/core';
import { DatasetResult, FlamingoTypes, MAX_SELECTED_FILTER_OPTS, ReportTypes } from '@mode/shared/contract-common';
import { isPresent, toDictionary } from '@mode/shared/util-js';
import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { Observable } from 'rxjs';
import { filter, map, shareReplay, withLatestFrom } from 'rxjs/operators';
import * as ReportFilterActions from './report-filter/report-filter.actions';
import * as fromReportFilterSelectors from './report-filter/report-filter.selectors';
import { LegacyReportFilterControl } from '@mode/filters/util';

// TODO: Move this and its ngrx state to the reports grouping

@Injectable({
  providedIn: 'root',
})
export class ReportFilterFacade {
  filters$ = this.store.select(fromReportFilterSelectors.selectFilterDefinitions).pipe(
    map((filtersObject) => {
      return Object.keys(filtersObject).map((filterToken) => filtersObject[filterToken]);
    })
  );

  filterControls$ = this.store.select(fromReportFilterSelectors.selectFilterControls);

  isFiltersLoading$ = this.store.select(fromReportFilterSelectors.selectIsFiltersLoading);
  isFiltersComplete$ = this.store.select(fromReportFilterSelectors.selectIsFiltersComplete);

  // Experimental pattern for allowing features to open modals from arbitrary ngrx actions. Do not blindly copy.
  applyFilterConfirmation$: Observable<{ fields: ReadonlyArray<string>; confirm: () => void; cancel: () => void }>;
  applyFiltersAndGenerateLinkConfirmation$: Observable<{
    fields: ReadonlyArray<string>;
    confirm: () => void;
    cancel: () => void;
  }>;
  readonly hasFilters$: Observable<boolean>;
  constructor(
    private store: Store<fromReportFilterSelectors.ReportFilterDomainState>,
    private actions$: Actions
  ) {
    const filterConfirmation$ = this.actions$.pipe(
      ofType(ReportFilterActions.requestApplyReportFilters, ReportFilterActions.requestUpdateReportFilterDefaults),
      withLatestFrom(this.store.select(fromReportFilterSelectors.selectFilterDefinitions)),
      map(([action, filters]) => {
        // determine any filter selections that have too many items in them
        const oversized: Array<string> = [];
        Object.keys(action.selected).map((token) => {
          const selected = action.selected[token];
          if (selected != null && 'values' in selected) {
            if (selected.values?.length && selected.values?.length > MAX_SELECTED_FILTER_OPTS) {
              oversized.push(filters[token].name);
            }
          }
        });

        let confirm = () => this.store.dispatch(ReportFilterActions.applyReportFilters({ selected: action.selected }));
        if (action.type === ReportFilterActions.requestUpdateReportFilterDefaults.type) {
          confirm = () =>
            this.store.dispatch(ReportFilterActions.updateReportFilterDefaults({ defaults: action.selected }));
        }

        return {
          fields: oversized,
          confirm,
          cancel: () => void 0,
        };
      }),
      shareReplay(1)
    );
    // If there are no oversized selections, just autoconfirm
    filterConfirmation$.pipe(filter((data) => data.fields.length <= 0)).subscribe(({ confirm }) => confirm());

    this.applyFilterConfirmation$ = filterConfirmation$.pipe(filter((data) => data.fields.length > 0));

    this.applyFiltersAndGenerateLinkConfirmation$ = this.actions$.pipe(
      ofType(ReportFilterActions.filtersRequireConfirmation),
      map(({ oversizedFields, selected }) => ({
        fields: oversizedFields,
        confirm: () => this.store.dispatch(ReportFilterActions.requestApplyReportFiltersAndGenerateLink({ selected })),
        cancel: () => {},
      }))
    );
    this.hasFilters$ = store.select(fromReportFilterSelectors.hasFilters).pipe(filter(isPresent), shareReplay(1));
  }

  loadReportFilters(reportToken: string) {
    // This action only sets the report filter loading state.
    this.store.dispatch(ReportFilterActions.loadReportFilters());
  }

  loadReportFilterResources(resources: ReportTypes.ReportFilterResource[]) {
    this.store.dispatch(
      ReportFilterActions.loadReportFiltersSuccess({
        resources,
      })
    );
  }

  loadReportFiltersFailure(error: unknown) {
    this.store.dispatch(
      ReportFilterActions.loadReportFiltersFailure({
        error,
      })
    );
  }

  createReportFilter(accountUsername: string, reportToken: string, filterField: ReportTypes.ReportFilterField) {
    this.store.dispatch(
      ReportFilterActions.createReportFilter({
        accountUsername,
        reportToken,
        filterField,
      })
    );
  }

  destroyReportFilter(token: string) {
    this.store.dispatch(
      ReportFilterActions.destroyReportFilter({
        token,
      })
    );
  }

  updateReportFilterType(
    token: string,
    filterType: ReportTypes.ReportFilterType,
    existingSelection: ReportTypes.FilterSelection
  ) {}

  loadFilterOptions(filterOptions: { token: string; options: ReportTypes.ReportFilterOptions }) {
    this.store.dispatch(
      ReportFilterActions.loadReportFilterOptsSuccess({
        options: toDictionary([filterOptions], (option) => {
          switch (option.options.filterType) {
            case ReportTypes.ReportFilterType.Range:
              return {
                filterType: option.options.filterType,
                values: {
                  min: option.options.values.min,
                  max: option.options.values.max,
                },
              };
            case ReportTypes.ReportFilterType.Single:
              return {
                filterType: option.options.filterType,
                values: option.options.values,
              };
            case ReportTypes.ReportFilterType.Multi:
              return {
                filterType: option.options.filterType,
                values: option.options.values,
              };
          }
        }),
      })
    );
  }

  notReadyForDomainQuery(
    datasetResult: DatasetResult,
    filters: ReportTypes.FilterDefinition[],
    model: FlamingoTypes.ModeModel
  ): void {
    this.store.dispatch(ReportFilterActions.notReadyForDomainQuery({ datasetResult, filters, model }));
  }

  saveSharedAppliedFilter(filters: LegacyReportFilterControl[]) {
    this.store.dispatch(ReportFilterActions.filtersApplyAndGenerateLink({ filters: filters }));
  }
}
